import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { fetchContent, subscribeToType, LIVE_MODE } from '../lib/sanity'

interface CTAContent {
  heading: string
  description: string
  buttonText: string
  buttonUrl: string
}

const CTA = () => {
  const [content, setContent] = useState<CTAContent | null>(null)

  useEffect(() => {
    let unsubscribe: (() => void) | undefined
    const getContent = async () => {
      try {
        const [data] = await fetchContent('cta')
        setContent(data)
        if (LIVE_MODE) {
          unsubscribe = subscribeToType('cta', (docs) => setContent(docs[0]))
        }
      } catch (error) {
        console.error('Error fetching CTA content:', error)
      }
    }
    getContent()
    return () => {
      if (unsubscribe) unsubscribe()
    }
  }, [])

  return (
    <section className="relative isolate overflow-hidden bg-[#cdfbf1] py-24 text-slate-900">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.9),_rgba(205,251,241,0.6))]" />
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-white to-transparent opacity-70" />

      <div className="container relative z-10 mx-auto px-6">
        <div className="rounded-[36px] border border-white/80 bg-white/90 p-10 shadow-[0_25px_70px_rgba(6,96,94,0.15)]">
          <p className="text-xs uppercase tracking-[0.4em] text-teal-700">Now onboarding</p>
          <div className="mt-4 grid gap-10 lg:grid-cols-[1.3fr_0.7fr] lg:items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-semibold text-slate-900">
                {content?.heading || 'Choose your next step'}
              </h2>
              <p className="mt-4 text-base text-slate-600 leading-relaxed">
                {content?.description || 'Remote support includes practical onboarding, routine planning, and Andrea by your side as your habits evolve alongside licensed clinical care.'}
              </p>
            </div>
            <div className="space-y-4">
              <motion.a
                href={content?.buttonUrl || 'https://elliemd.com/joyfulhealth'}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex w-full items-center justify-center rounded-2xl bg-gradient-to-r from-teal-400 to-cyan-300 px-8 py-4 text-lg font-semibold text-slate-900"
              >
                {content?.buttonText || 'Explore accountability support'}
              </motion.a>
              <a
                href="#contact"
                className="block w-full rounded-2xl border border-teal-100 px-8 py-4 text-center text-sm font-semibold text-teal-800 hover:bg-[#cdfbf1]"
              >
                Want to chat first? Send a quick note below.
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default CTA
